import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import {
  accountsQueryOptions,
  breedsQueryOptions,
  colorsQueryOptions,
  diagnosesQueryOptions,
  drugsQueryOptions,
  pensQueryOptions,
  yardsQueryOptions,
} from '@/services/animal-management/api';
import { useAccessToken } from '@/services/auth';

export type LookupKind =
  | 'breed'
  | 'owner'
  | 'yard'
  | 'pen'
  | 'drug'
  | 'color'
  | 'diagnosis';

type LookupOption = { id: string; name: string };
type RawLookupOption = { id: string; name: string | null };

const QUERIES = {
  breed: breedsQueryOptions,
  owner: accountsQueryOptions,
  yard: yardsQueryOptions,
  pen: pensQueryOptions,
  drug: drugsQueryOptions,
  color: colorsQueryOptions,
  diagnosis: diagnosesQueryOptions,
} as const;

type LookupComboboxProps = {
  id: string;
  kind: LookupKind;
  value: string | null;
  onChange: (id: string | null) => void;
  /** Label for the current value, so it renders before any search runs. */
  selectedLabel?: string;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** Called with the full API option (e.g. diagnosis drug defaults) on select. */
  onSelectOption?: (option: RawLookupOption | null) => void;
};

export function LookupCombobox({
  id,
  kind,
  value,
  onChange,
  selectedLabel,
  placeholder,
  invalid,
  disabled,
  onSelectOption,
}: LookupComboboxProps) {
  const { t } = useTranslation();
  const token = useAccessToken();
  const [search, setSearch] = useState('');
  const { data = [] } = useQuery(
    (QUERIES[kind] as typeof breedsQueryOptions)(token, { search }),
  );

  const items = useMemo<LookupOption[]>(() => {
    const options: LookupOption[] = data.map((o) => ({
      id: o.id,
      name: o.name ?? '',
    }));
    if (value && selectedLabel && !options.some((o) => o.id === value)) {
      options.unshift({ id: value, name: selectedLabel });
    }
    return options;
  }, [data, value, selectedLabel]);

  return (
    <Combobox
      items={items}
      autoComplete="none"
      value={items.find((o) => o.id === value) ?? null}
      onValueChange={(option: LookupOption | null) => {
        onChange(option?.id ?? null);
        onSelectOption?.(
          option ? (data.find((o) => o.id === option.id) ?? option) : null,
        );
      }}
      itemToStringValue={(option: LookupOption) => option.id}
      itemToStringLabel={(option: LookupOption) => option.name}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        aria-invalid={invalid}
        className="h-full w-full"
        placeholder={placeholder}
        disabled={disabled}
        showClear={!disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
      />
      <ComboboxContent>
        <ComboboxEmpty>
          {t('common.noItemsFound', 'No items found.')}
        </ComboboxEmpty>
        <ComboboxList>
          {(option: LookupOption) => (
            <ComboboxItem key={option.id} value={option}>
              {option.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
